<template>
    <div class="token-supply-events">
        <!-- Loading state -->
        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>

        <template v-else>
            <!-- Supply Metrics Cards -->
            <div class="supply-metrics-grid">
                <div class="compass-stat-card">
                    <div class="stat-label">Current Supply</div>
                    <div class="stat-value font-mono-num">{{ formatSupply(supplyMetrics.currentSupply) }}</div>
                    <div class="stat-unit" v-if="tokenSymbol">{{ tokenSymbol }}</div>
                </div>
                <div class="compass-stat-card compass-stat-card--positive" v-if="supplyMetrics.totalMinted > 0">
                    <div class="stat-label">Total Minted</div>
                    <div class="stat-value font-mono-num">+{{ formatSupply(supplyMetrics.totalMinted) }}</div>
                    <div class="stat-sub" v-if="supplyMetrics.mintCount">{{ supplyMetrics.mintCount }} events</div>
                </div>
                <div class="compass-stat-card compass-stat-card--negative" v-if="supplyMetrics.totalBurned > 0">
                    <div class="stat-label">Total Burned</div>
                    <div class="stat-value font-mono-num">-{{ formatSupply(supplyMetrics.totalBurned) }}</div>
                    <div class="stat-sub" v-if="supplyMetrics.burnCount">{{ supplyMetrics.burnCount }} events</div>
                </div>
                <div class="compass-stat-card" :class="netChangeClass">
                    <div class="stat-label">Net Change ({{ rangeLabel }})</div>
                    <div class="stat-value font-mono-num">{{ netChangeFormatted }}</div>
                    <div class="stat-sub" v-if="supplyMetrics.totalEvents > 0">{{ supplyMetrics.totalEvents }} total events</div>
                </div>
            </div>

            <!-- Authority Info -->
            <div class="card compass-styled compass-styled--accent mb-4" v-if="securityData">
                <div class="card-header">
                    <h5 class="card-title mb-0">Authority Info</h5>
                </div>
                <div class="card-body">
                    <div class="authority-grid">
                        <!-- Mint Authority -->
                        <div class="authority-item">
                            <div class="authority-label">Mint Authority</div>
                            <div class="authority-value">
                                <span v-if="!securityData.is_mintable" class="authority-status authority-status--safe">
                                    <span class="status-dot status-dot--safe"></span>
                                    Disabled — No new tokens can be minted
                                </span>
                                <template v-else>
                                    <span class="authority-status" :class="mintAuthorityStatusClass">
                                        <span class="status-dot" :class="mintAuthorityDotClass"></span>
                                        {{ mintAuthorityLabel }}
                                    </span>
                                    <a :href="`https://solscan.io/account/${securityData.mint_authority}`"
                                       target="_blank"
                                       class="authority-address font-mono-num"
                                       :title="securityData.mint_authority">
                                        {{ securityData.mint_authority }}
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="external-icon">
                                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                            <polyline points="15 3 21 3 21 9"/>
                                            <line x1="10" y1="14" x2="21" y2="3"/>
                                        </svg>
                                    </a>
                                    <span v-if="securityData.mint_authority_name" class="authority-name">
                                        {{ securityData.mint_authority_name }}
                                    </span>
                                </template>
                            </div>
                        </div>

                        <!-- Freeze Authority -->
                        <div class="authority-item">
                            <div class="authority-label">Freeze Authority</div>
                            <div class="authority-value">
                                <span v-if="!securityData.is_freezable" class="authority-status authority-status--safe">
                                    <span class="status-dot status-dot--safe"></span>
                                    Disabled — Accounts cannot be frozen
                                </span>
                                <template v-else>
                                    <span class="authority-status" :class="freezeAuthorityStatusClass">
                                        <span class="status-dot" :class="freezeAuthorityDotClass"></span>
                                        {{ freezeAuthorityLabel }}
                                    </span>
                                    <a :href="`https://solscan.io/account/${securityData.freeze_authority}`"
                                       target="_blank"
                                       class="authority-address font-mono-num"
                                       :title="securityData.freeze_authority">
                                        {{ securityData.freeze_authority }}
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="external-icon">
                                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                            <polyline points="15 3 21 3 21 9"/>
                                            <line x1="10" y1="14" x2="21" y2="3"/>
                                        </svg>
                                    </a>
                                    <span v-if="securityData.freeze_authority_name" class="authority-name">
                                        {{ securityData.freeze_authority_name }}
                                    </span>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Supply Timeline Chart -->
            <div class="card compass-styled mb-4" v-if="chartData.labels.length > 1">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <h5 class="card-title mb-0">Supply Timeline</h5>
                    <div class="chart-range-selector">
                        <button
                            v-for="r in rangeOptions"
                            :key="r.value"
                            class="range-btn"
                            :class="{ active: selectedRange === r.value }"
                            @click="changeRange(r.value)"
                        >
                            {{ r.label }}
                        </button>
                    </div>
                </div>
                <div class="card-body">
                    <div class="chart-container">
                        <canvas ref="supplyChart"></canvas>
                    </div>
                </div>
            </div>

            <!-- No chart data info -->
            <div class="compass-info-box mb-4" v-else-if="events.length === 0">
                <h5>No Supply Events Found</h5>
                <p>No mint, burn, or freeze events have been recorded for this token in the selected period.
                   This may mean the token supply has remained unchanged, or event data is not yet available.</p>
            </div>

            <!-- Event Feed Table -->
            <div class="card compass-styled mb-4">
                <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h5 class="card-title mb-0">Event Feed</h5>
                    <div class="event-filters">
                        <button
                            v-for="filter in eventFilterOptions"
                            :key="filter.value"
                            class="filter-btn"
                            :class="{ active: activeFilter === filter.value }"
                            @click="setFilter(filter.value)"
                        >
                            <span class="filter-dot" :class="`filter-dot--${filter.value}`"></span>
                            {{ filter.label }}
                        </button>
                    </div>
                </div>
                <div class="card-body p-0 table-mobile-scroll">
                    <table class="table table-sm compass-table mb-0" v-if="filteredEvents.length > 0">
                        <thead>
                            <tr>
                                <th>Time</th>
                                <th>Type</th>
                                <th class="text-end">Amount</th>
                                <th class="d-none d-md-table-cell">Authority</th>
                                <th class="text-end">Transaction</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="event in filteredEvents" :key="event.signature + event.timestamp">
                                <td class="td-num">
                                    <span :title="event.timestamp">{{ formatTime(event.timestamp) }}</span>
                                </td>
                                <td>
                                    <span class="event-badge" :class="`event-badge--${event.event_type}`">
                                        {{ event.event_type }}
                                    </span>
                                </td>
                                <td class="text-end td-num font-mono-num">
                                    <span v-if="event.amount !== null" :class="amountClass(event.event_type)">
                                        {{ amountPrefix(event.event_type) }}{{ formatEventAmount(event) }}
                                    </span>
                                    <span v-else class="text-muted">—</span>
                                </td>
                                <td class="d-none d-md-table-cell">
                                    <a v-if="event.authority"
                                       :href="`https://solscan.io/account/${event.authority}`"
                                       target="_blank"
                                       class="authority-link font-mono-num"
                                       :title="event.authority">
                                        {{ truncateAddress(event.authority) }}
                                    </a>
                                    <span v-else class="text-muted">—</span>
                                </td>
                                <td class="text-end">
                                    <a v-if="event.signature"
                                       :href="`https://solscan.io/tx/${event.signature}`"
                                       target="_blank"
                                       class="tx-link font-mono-num"
                                       :title="event.signature">
                                        {{ truncateAddress(event.signature) }}
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="external-icon">
                                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                            <polyline points="15 3 21 3 21 9"/>
                                            <line x1="10" y1="14" x2="21" y2="3"/>
                                        </svg>
                                    </a>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-else class="text-center py-4 text-muted">
                        <p class="mb-0">No {{ activeFilter === 'all' ? '' : activeFilter + ' ' }}events found in this period.</p>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import Chart from 'chart.js/auto';

export default {
    name: 'TokenSupplyEvents',

    props: {
        mint: {
            type: String,
            required: true,
        },
        tokenSymbol: {
            type: String,
            default: '',
        },
        decimals: {
            type: Number,
            default: null,
        },
        currentSupply: {
            type: [Number, String],
            default: null,
        },
    },

    data() {
        return {
            loading: true,
            events: [],
            securityData: null,
            selectedRange: '90d',
            activeFilter: 'all',
            chart: null,
            rangeOptions: [
                { label: '7D', value: '7d' },
                { label: '30D', value: '30d' },
                { label: '90D', value: '90d' },
                { label: '1Y', value: '365d' },
            ],
            eventFilterOptions: [
                { label: 'All', value: 'all' },
                { label: 'Mint', value: 'mint' },
                { label: 'Burn', value: 'burn' },
                { label: 'Freeze', value: 'freeze' },
                { label: 'Thaw', value: 'thaw' },
            ],
        };
    },

    computed: {
        supplyMetrics() {
            let totalMinted = 0;
            let totalBurned = 0;
            let mintCount = 0;
            let burnCount = 0;

            for (const event of this.events) {
                const amount = this.parseAmount(event);
                if (event.event_type === 'mint' && amount > 0) {
                    totalMinted += amount;
                    mintCount++;
                } else if (event.event_type === 'burn' && amount > 0) {
                    totalBurned += amount;
                    burnCount++;
                }
            }

            return {
                currentSupply: this.currentSupply,
                totalMinted,
                totalBurned,
                netChange: totalMinted - totalBurned,
                mintCount,
                burnCount,
                totalEvents: this.events.length,
            };
        },

        netChangeFormatted() {
            const net = this.supplyMetrics.netChange;
            if (net === 0) return '0';
            const prefix = net > 0 ? '+' : '';
            return prefix + this.formatSupply(net);
        },

        netChangeClass() {
            const net = this.supplyMetrics.netChange;
            if (net > 0) return 'compass-stat-card--positive';
            if (net < 0) return 'compass-stat-card--negative';
            return '';
        },

        rangeLabel() {
            const match = this.rangeOptions.find(r => r.value === this.selectedRange);
            return match ? match.label : this.selectedRange;
        },

        filteredEvents() {
            if (this.activeFilter === 'all') return this.events;
            return this.events.filter(e => e.event_type === this.activeFilter);
        },

        chartData() {
            // Build cumulative supply change from events (sorted oldest first)
            const sorted = [...this.events]
                .filter(e => e.event_type === 'mint' || e.event_type === 'burn')
                .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

            if (sorted.length === 0) {
                return { labels: [], data: [] };
            }

            // Aggregate by day
            const dailyMap = new Map();
            for (const event of sorted) {
                const day = event.timestamp.substring(0, 10);
                if (!dailyMap.has(day)) {
                    dailyMap.set(day, { minted: 0, burned: 0 });
                }
                const amount = this.parseAmount(event);
                if (event.event_type === 'mint') {
                    dailyMap.get(day).minted += amount;
                } else if (event.event_type === 'burn') {
                    dailyMap.get(day).burned += amount;
                }
            }

            const labels = [];
            const data = [];
            let cumulative = 0;

            for (const [day, { minted, burned }] of dailyMap) {
                cumulative += minted - burned;
                labels.push(day);
                data.push(cumulative);
            }

            return { labels, data };
        },

        // Authority computed properties
        mintAuthorityStatusClass() {
            if (!this.securityData) return '';
            return this.securityData.mint_authority_is_pda
                ? 'authority-status--warning'
                : 'authority-status--danger';
        },
        mintAuthorityDotClass() {
            if (!this.securityData) return '';
            return this.securityData.mint_authority_is_pda
                ? 'status-dot--warning'
                : 'status-dot--danger';
        },
        mintAuthorityLabel() {
            if (!this.securityData) return '';
            return this.securityData.mint_authority_is_pda
                ? 'Program Controlled (PDA)'
                : 'Wallet Controlled';
        },
        freezeAuthorityStatusClass() {
            if (!this.securityData) return '';
            return this.securityData.freeze_authority_is_pda
                ? 'authority-status--warning'
                : 'authority-status--danger';
        },
        freezeAuthorityDotClass() {
            if (!this.securityData) return '';
            return this.securityData.freeze_authority_is_pda
                ? 'status-dot--warning'
                : 'status-dot--danger';
        },
        freezeAuthorityLabel() {
            if (!this.securityData) return '';
            return this.securityData.freeze_authority_is_pda
                ? 'Program Controlled (PDA)'
                : 'Wallet Controlled';
        },
    },

    mounted() {
        this.fetchAll();
    },

    watch: {
        mint() {
            this.fetchAll();
        },
    },

    beforeUnmount() {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    },

    methods: {
        async fetchAll() {
            this.loading = true;
            try {
                await Promise.all([
                    this.fetchEvents(),
                    this.fetchSecurity(),
                ]);
            } finally {
                this.loading = false;
                this.$nextTick(() => this.renderChart());
            }
        },

        async fetchEvents() {
            try {
                const response = await fetch(
                    `/api/v1/tokens/${this.mint}/events?range=${this.selectedRange}&limit=200`
                );
                if (response.ok) {
                    const json = await response.json();
                    this.events = json.data?.events ?? [];
                } else {
                    this.events = [];
                }
            } catch (error) {
                console.error('Failed to fetch token events:', error);
                this.events = [];
            }
        },

        async fetchSecurity() {
            try {
                const response = await fetch(`/api/tokens/${this.mint}/security`);
                if (response.ok) {
                    this.securityData = await response.json();
                }
            } catch (error) {
                console.error('Failed to fetch security data:', error);
            }
        },

        async changeRange(range) {
            this.selectedRange = range;
            this.loading = true;
            try {
                await this.fetchEvents();
            } finally {
                this.loading = false;
                this.$nextTick(() => this.renderChart());
            }
        },

        setFilter(filter) {
            this.activeFilter = filter;
        },

        parseAmount(event) {
            if (event.amount === null || event.amount === undefined) return 0;
            const raw = parseFloat(event.amount);
            if (isNaN(raw)) return 0;
            const decimals = event.decimals ?? this.decimals ?? 0;
            return raw / Math.pow(10, decimals);
        },

        formatSupply(value) {
            if (value === null || value === undefined) return '—';
            const num = parseFloat(value);
            if (isNaN(num)) return '—';
            if (Math.abs(num) >= 1e12) return (num / 1e12).toFixed(2) + 'T';
            if (Math.abs(num) >= 1e9) return (num / 1e9).toFixed(2) + 'B';
            if (Math.abs(num) >= 1e6) return (num / 1e6).toFixed(2) + 'M';
            if (Math.abs(num) >= 1e3) return (num / 1e3).toFixed(2) + 'K';
            if (Number.isInteger(num)) return num.toLocaleString();
            return num.toFixed(2);
        },

        formatEventAmount(event) {
            const amount = this.parseAmount(event);
            return this.formatSupply(amount);
        },

        amountPrefix(eventType) {
            if (eventType === 'mint') return '+';
            if (eventType === 'burn') return '-';
            return '';
        },

        amountClass(eventType) {
            if (eventType === 'mint') return 'amount-mint';
            if (eventType === 'burn') return 'amount-burn';
            return '';
        },

        formatTime(timestamp) {
            if (!timestamp) return '—';
            const date = new Date(timestamp);
            const now = new Date();
            const diffMs = now - date;
            const diffHours = diffMs / (1000 * 60 * 60);

            if (diffHours < 24) {
                const hours = Math.floor(diffHours);
                if (hours < 1) {
                    const mins = Math.floor(diffMs / (1000 * 60));
                    return `${mins}m ago`;
                }
                return `${hours}h ago`;
            }

            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
            }) + ' ' + date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            });
        },

        truncateAddress(address) {
            if (!address) return '—';
            return `${address.slice(0, 4)}...${address.slice(-4)}`;
        },

        renderChart() {
            if (this.chart) {
                this.chart.destroy();
                this.chart = null;
            }

            const canvas = this.$refs.supplyChart;
            if (!canvas || this.chartData.labels.length < 2) return;

            const colors = window.divergentColor || ['#8b5cf6', '#10f7cf'];
            const gradientColor = colors[0] || '#8b5cf6';

            const ctx = canvas.getContext('2d');
            const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height || 300);
            gradient.addColorStop(0, gradientColor + '33');
            gradient.addColorStop(1, gradientColor + '05');

            this.chart = new Chart(ctx, {
                type: 'line',
                data: {
                    labels: this.chartData.labels,
                    datasets: [{
                        label: 'Cumulative Supply Change',
                        data: this.chartData.data,
                        borderColor: gradientColor,
                        backgroundColor: gradient,
                        borderWidth: 2,
                        fill: true,
                        tension: 0.3,
                        pointRadius: this.chartData.data.length > 30 ? 0 : 3,
                        pointHoverRadius: 5,
                        pointBackgroundColor: gradientColor,
                    }],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        intersect: false,
                        mode: 'index',
                    },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            backgroundColor: 'rgba(26, 26, 46, 0.95)',
                            borderColor: 'rgba(139, 92, 246, 0.3)',
                            borderWidth: 1,
                            titleColor: '#e6edf3',
                            bodyColor: '#e6edf3',
                            padding: 12,
                            displayColors: false,
                            callbacks: {
                                label: (context) => {
                                    const val = context.parsed.y;
                                    const prefix = val >= 0 ? '+' : '';
                                    return `Net change: ${prefix}${this.formatSupply(val)}`;
                                },
                            },
                        },
                    },
                    scales: {
                        x: {
                            grid: {
                                color: 'rgba(139, 92, 246, 0.08)',
                            },
                            ticks: {
                                color: '#7d8590',
                                maxTicksLimit: 8,
                                font: { size: 11 },
                            },
                        },
                        y: {
                            grid: {
                                color: 'rgba(139, 92, 246, 0.08)',
                            },
                            ticks: {
                                color: '#7d8590',
                                font: {
                                    size: 11,
                                    family: "'JetBrains Mono', 'SF Mono', monospace",
                                },
                                callback: (value) => this.formatSupply(value),
                            },
                        },
                    },
                },
            });
        },
    },
};
</script>

<style scoped>
/* Supply Metrics Grid */
.supply-metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
}

/* Chart container */
.chart-container {
    position: relative;
    height: 300px;
}

/* Range selector buttons */
.chart-range-selector {
    display: flex;
    gap: 0.25rem;
    background: rgba(139, 92, 246, 0.08);
    border-radius: var(--compass-radius-sm, 8px);
    padding: 0.15rem;
}

.range-btn {
    padding: 0.3rem 0.7rem;
    border: none;
    background: transparent;
    color: var(--compass-text-secondary, #7d8590);
    font-size: 0.75rem;
    font-weight: 600;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.range-btn:hover:not(.active) {
    color: var(--compass-text-primary, #e6edf3);
    background: rgba(139, 92, 246, 0.1);
}

.range-btn.active {
    background: linear-gradient(135deg, var(--compass-purple, #8b5cf6), var(--compass-pink, #ec4899));
    color: #fff;
}

/* Authority grid */
.authority-grid {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

.authority-item {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
}

.authority-label {
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--compass-text-secondary, #7d8590);
}

.authority-value {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.authority-status {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.875rem;
    font-weight: 500;
}

.authority-status--safe { color: var(--compass-success, #22c55e); }
.authority-status--warning { color: #f59e0b; }
.authority-status--danger { color: var(--compass-danger, #ef4444); }

.status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
    flex-shrink: 0;
}

.status-dot--safe { background: var(--compass-success, #22c55e); }
.status-dot--warning { background: #f59e0b; }
.status-dot--danger { background: var(--compass-danger, #ef4444); }

.authority-address {
    font-size: 0.8rem;
    color: var(--compass-purple, #8b5cf6);
    text-decoration: none;
    word-break: break-all;
    transition: opacity 0.15s ease;
}

.authority-address:hover {
    opacity: 0.8;
    text-decoration: underline;
}

.authority-name {
    font-size: 0.8rem;
    color: var(--compass-text-secondary, #7d8590);
}

.external-icon {
    margin-left: 0.25rem;
    opacity: 0.6;
    vertical-align: middle;
}

/* Event filters */
.event-filters {
    display: flex;
    gap: 0.25rem;
    flex-wrap: wrap;
}

.filter-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.3rem 0.65rem;
    border: 1px solid var(--compass-border, rgba(139, 92, 246, 0.2));
    background: transparent;
    color: var(--compass-text-secondary, #7d8590);
    font-size: 0.75rem;
    font-weight: 500;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.filter-btn:hover:not(.active) {
    border-color: var(--compass-purple, #8b5cf6);
    color: var(--compass-text-primary, #e6edf3);
}

.filter-btn.active {
    background: rgba(139, 92, 246, 0.15);
    border-color: var(--compass-purple, #8b5cf6);
    color: var(--compass-text-primary, #e6edf3);
}

.filter-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
}

.filter-dot--all { background: var(--compass-text-secondary, #7d8590); }
.filter-dot--mint { background: var(--compass-success, #22c55e); }
.filter-dot--burn { background: var(--compass-danger, #ef4444); }
.filter-dot--freeze { background: #3b82f6; }
.filter-dot--thaw { background: #f59e0b; }

/* Event badges */
.event-badge {
    display: inline-block;
    padding: 0.15rem 0.5rem;
    border-radius: 4px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.event-badge--mint {
    background: rgba(34, 197, 94, 0.15);
    color: var(--compass-success, #22c55e);
}

.event-badge--burn {
    background: rgba(239, 68, 68, 0.15);
    color: var(--compass-danger, #ef4444);
}

.event-badge--freeze {
    background: rgba(59, 130, 246, 0.15);
    color: #3b82f6;
}

.event-badge--thaw {
    background: rgba(245, 158, 11, 0.15);
    color: #f59e0b;
}

/* Amount colors */
.amount-mint {
    color: var(--compass-success, #22c55e);
}

.amount-burn {
    color: var(--compass-danger, #ef4444);
}

/* Links */
.authority-link,
.tx-link {
    color: var(--compass-purple, #8b5cf6);
    text-decoration: none;
    font-size: 0.8rem;
    transition: opacity 0.15s ease;
}

.authority-link:hover,
.tx-link:hover {
    opacity: 0.8;
    text-decoration: underline;
}

/* Stat sub text */
.stat-sub {
    font-size: 0.75rem;
    color: var(--compass-text-secondary, #7d8590);
    margin-top: 0.15rem;
}

/* Light mode */
body:not(.dark-mode) .range-btn {
    color: #6b7280;
}

body:not(.dark-mode) .range-btn:hover:not(.active) {
    color: #374151;
}

body:not(.dark-mode) .range-btn.active {
    color: #fff;
}

body:not(.dark-mode) .filter-btn {
    color: #6b7280;
}

body:not(.dark-mode) .filter-btn:hover:not(.active),
body:not(.dark-mode) .filter-btn.active {
    color: #374151;
}

body:not(.dark-mode) .authority-label {
    color: #6b7280;
}

/* Mobile */
@media (max-width: 768px) {
    .supply-metrics-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .chart-container {
        height: 220px;
    }

    .authority-address {
        font-size: 0.7rem;
    }
}

@media (max-width: 480px) {
    .supply-metrics-grid {
        grid-template-columns: 1fr;
    }
}
</style>
